import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, KeyRound, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ForgotPasswordPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("email");
  const [form, setForm] = useState({ email: "", code: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const request = async (e) => {
    e?.preventDefault();
    setBusy(true); setError("");
    try { await api.post("/auth/forgot-password", { email: form.email }); setStep("code"); toast.success("If that email has an account, a code is on its way"); }
    catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  const reset = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return setError("The passwords don't match");
    setBusy(true); setError("");
    try {
      await signIn("/auth/reset-password", { email: form.email, code: form.code, password: form.password });
      toast.success("Password updated. You've been signed out everywhere else.");
      navigate("/");
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center lf-glow lf-grain p-6" data-testid="forgot-password-page">
      <div className="relative z-10 w-full max-w-md rounded-md border border-[#2D3442] bg-[#0F131A]/90 p-8 backdrop-blur-xl">
        <Link to="/login" className="flex items-center gap-1 text-xs text-[#A8A090] hover:text-[#F3EBDD]" data-testid="forgot-back-link"><ChevronLeft className="h-3.5 w-3.5" />Back to sign in</Link>
        <KeyRound className="mt-6 h-8 w-8 text-[#F59E0B]" />
        <h1 className="mt-5 font-display text-4xl text-[#F3EBDD]">{step === "email" ? "Forgot your password?" : "Choose a new password"}</h1>
        {step === "email" ? (
          <form onSubmit={request} className="mt-6 space-y-5">
            <p className="text-sm text-[#A8A090]">Enter your account email and we'll send you a 6-digit reset code.</p>
            <div className="space-y-2"><Label htmlFor="fp-email">Email</Label>
              <Input id="fp-email" type="email" required value={form.email} onChange={set("email")} data-testid="forgot-email-input" /></div>
            {error && <p className="text-sm text-[#F87171]" data-testid="forgot-error">{error}</p>}
            <Button type="submit" className="w-full" disabled={busy} data-testid="forgot-send-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Send reset code</Button>
          </form>
        ) : (
          <form onSubmit={reset} className="mt-6 space-y-5">
            <p className="text-sm text-[#A8A090]">Enter the code sent to <span className="text-[#F3EBDD]">{form.email}</span>, then your new password.</p>
            <div className="flex justify-center">
              <InputOTP maxLength={6} value={form.code} onChange={(v) => setForm({ ...form, code: v.replace(/\D/g, "") })} data-testid="reset-code-input" inputMode="numeric">
                <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11 text-lg" />)}</InputOTPGroup>
              </InputOTP>
            </div>
            <div className="space-y-2"><Label htmlFor="fp-pw">New password</Label>
              <Input id="fp-pw" type="password" required minLength={6} value={form.password} onChange={set("password")} data-testid="reset-password-input" /></div>
            <div className="space-y-2"><Label htmlFor="fp-pw2">Confirm new password</Label>
              <Input id="fp-pw2" type="password" required minLength={6} value={form.confirm} onChange={set("confirm")} data-testid="reset-confirm-input" /></div>
            {error && <p className="text-sm text-[#F87171]" data-testid="reset-error">{error}</p>}
            <Button type="submit" className="w-full" disabled={busy || form.code.length !== 6} data-testid="reset-submit-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Reset password &amp; sign in</Button>
            <div className="flex justify-between text-sm">
              <button type="button" onClick={request} disabled={busy} className="text-[#F59E0B] hover:underline" data-testid="reset-resend-button">Send a new code</button>
              <button type="button" onClick={() => { setStep("email"); setError(""); }} className="text-[#A8A090] hover:text-[#F3EBDD]" data-testid="reset-change-email">Use a different email</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
